import { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar
} from '@mui/material';
import { formatDayHeader, formatMonthYear } from '../utils/formatters';
import { 
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAppointments } from '../hooks/queries';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import AppointmentCard from '../components/AppointmentCard';
import useThemeLabels from '../hooks/useThemeLabels';
import { useContextualHint } from '../hooks/useContextualHint';
import ContextualHint from '../components/ContextualHint';

// Helper function defined outside component
function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day; // domingo = 0
  return new Date(d.setDate(diff));
}

export default function AppointmentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const daysToShow = isMobile ? 2 : 7;
  
  // Hook de labels do tema
  const { getLabel } = useThemeLabels();
  
  // Estados de navegação e UI
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Filtros (estados locais - não afetam a query)
  const [selectedProfessional, setSelectedProfessional] = useState('all');
  const [selectedPlace, setSelectedPlace] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Dialogs e notificações
  const [confirmDialog, setConfirmDialog] = useState({ open: false, appointment: null });
  const [markAttendedDialog, setMarkAttendedDialog] = useState({ open: false, appointment: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, appointment: null });
  const [rescheduleData, setRescheduleData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Calcula datas para a query
  const queryDates = useMemo(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + daysToShow);
    return {
      startDate: currentWeekStart.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }, [currentWeekStart, daysToShow]);

  // React Query hook - SEM CACHE (sempre busca dados frescos)
  const {
    appointments,
    professionals,
    places,
    loading,
    error,
    reload,
    confirmAsync,
    cancelAsync,
    markAttendedAsync,
  } = useAppointments(queryDates);

  const hint = useContextualHint('appointments', {});

  const handlePreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - daysToShow);
    setCurrentWeekStart(newStart);
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + daysToShow);
    setCurrentWeekStart(newStart);
  };

  const handleToday = () => {
    setCurrentWeekStart(getStartOfWeek(new Date()));
  };

  const handleAppointmentCreated = () => {
    setShowForm(false);
    reload();
  };

  const getDaysArray = () => {
    const days = [];
    for (let i = 0; i < daysToShow; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getAppointmentsForDay = (date) => {
    // Converter data do calendário para string YYYY-MM-DD em UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    let filtered = appointments.filter(apt => {
      if (!apt.startTime) return false; // Skip appointments sem startTime
      
      // Converter appointment UTC para BRT (UTC-3)
      const aptDate = new Date(apt.startTime);
      if (isNaN(aptDate.getTime())) return false; // Skip datas inválidas
      
      // Ajustar para timezone BRT (UTC-3)
      const aptDateBRT = new Date(aptDate.getTime() - (3 * 60 * 60 * 1000));
      const aptYear = aptDateBRT.getUTCFullYear();
      const aptMonth = String(aptDateBRT.getUTCMonth() + 1).padStart(2, '0');
      const aptDay = String(aptDateBRT.getUTCDate()).padStart(2, '0');
      const aptDateStr = `${aptYear}-${aptMonth}-${aptDay}`;
      
      return aptDateStr === dateStr;
    });
    
    // Aplicar filtro de profissional
    if (selectedProfessional !== 'all') {
      filtered = filtered.filter(apt => 
        apt.schedule?.professionalName && 
        professionals.find(p => p.id === selectedProfessional)?.name === apt.schedule.professionalName
      );
    }
    
    // Aplicar filtro de local
    if (selectedPlace !== 'all') {
      filtered = filtered.filter(apt => 
        apt.schedule?.placeName && 
        places.find(p => p.id === selectedPlace)?.name === apt.schedule.placeName
      );
    }
    
    // ✅ Aplicar filtro de status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(apt => {
        const status = getAppointmentStatus(apt);
        return status === selectedStatus;
      });
    }
    
    return filtered;
  };

  // ✅ Helper para determinar status do appointment
  const getAppointmentStatus = (appointment) => {
    if (appointment.cancelledAt) return 'cancelled';
    if (appointment.attendedAt) return 'completed';
    
    // Se passou da hora e não foi atendido = no_show
    const appointmentTime = new Date(appointment.startTime);
    const now = new Date();
    if (now > appointmentTime && !appointment.attendedAt && appointment.confirmedAt) {
      return 'no_show';
    }
    
    if (appointment.confirmedAt) return 'confirmed';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'success';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      case 'Completed': return 'info';
      default: return 'default';
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  // =============== AÇÕES DE APPOINTMENT ===============

  const handleConfirmClick = (appointment) => {
    setConfirmDialog({ open: true, appointment });
  };

  const handleConfirmAppointment = async () => {
    const { appointment } = confirmDialog;
    try {
      await confirmAsync(appointment.id);
      setSnackbar({ open: true, message: 'Agendamento confirmado com sucesso!', severity: 'success' });
    } catch (err) {
      console.error('Erro ao confirmar agendamento:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Erro ao confirmar agendamento', 
        severity: 'error' 
      });
    } finally {
      setConfirmDialog({ open: false, appointment: null });
    }
  };

  const handleMarkAttendedClick = (appointment) => {
    setMarkAttendedDialog({ open: true, appointment });
  };

  const handleMarkAttended = async () => {
    const { appointment } = markAttendedDialog;
    if (!appointment) return;
    try {
      await markAttendedAsync(appointment.id);
      setSnackbar({ open: true, message: 'Agendamento marcado como atendido!', severity: 'success' });
    } catch (err) {
      console.error('Erro ao marcar como atendido:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Erro ao marcar como atendido', 
        severity: 'error' 
      });
    } finally {
      setMarkAttendedDialog({ open: false, appointment: null });
    }
  };

  const handleCancelClick = (appointment) => {
    setCancelDialog({ open: true, appointment });
  };

  const handleCancelAppointment = async () => {
    const { appointment } = cancelDialog;
    if (!appointment) return;
    try {
      await cancelAsync({ id: appointment.id, reason: 'Cancelado pelo profissional' });
      setSnackbar({ open: true, message: 'Agendamento cancelado com sucesso!', severity: 'success' });
    } catch (err) {
      console.error('Erro ao cancelar agendamento:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Erro ao cancelar agendamento', 
        severity: 'error' 
      });
    } finally {
      setCancelDialog({ open: false, appointment: null });
    }
  };

  const handleRescheduleClick = (appointment) => {
    // Abrir modal de agendamento com dados pré-preenchidos
    setRescheduleData({
      clientName: appointment.clientName,
      clientPhone: appointment.clientPhone,
      clientEmail: appointment.clientEmail,
      clientNotes: appointment.clientNotes
    });
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setRescheduleData(null);
    setSelectedDate(null);
  };

  const days = getDaysArray();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {getLabel('appointments')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getLabel('appointmentsDescription')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(true)}
        >
          Novo Agendamento
        </Button>
      </Box>

      <ContextualHint hint={hint} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Filtros:
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Profissional</InputLabel>
            <Select
              value={selectedProfessional}
              onChange={(e) => setSelectedProfessional(e.target.value)}
              label="Profissional"
            >
              <MenuItem value="all">
                <em>Todos</em>
              </MenuItem>
              {professionals.map((prof) => (
                <MenuItem key={prof.id} value={prof.id}>
                  {prof.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Local</InputLabel>
            <Select
              value={selectedPlace}
              onChange={(e) => setSelectedPlace(e.target.value)}
              label="Local"
            >
              <MenuItem value="all">
                <em>Todos</em>
              </MenuItem>
              {places.map((place) => (
                <MenuItem key={place.id} value={place.id}>
                  {place.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">
                <em>Todos</em>
              </MenuItem>
              <MenuItem value="pending">
                🟠 Não Confirmados
              </MenuItem>
              <MenuItem value="confirmed">
                🟢 Confirmados
              </MenuItem>
              <MenuItem value="completed">
                🔵 Concluídos
              </MenuItem>
              <MenuItem value="no_show">
                🔴 Não Compareceram
              </MenuItem>
              <MenuItem value="cancelled">
                ⚪ Cancelados
              </MenuItem>
            </Select>
          </FormControl>
          
          {(selectedProfessional !== 'all' || selectedPlace !== 'all' || selectedStatus !== 'all') && (
            <Button 
              variant="outlined"
              size="small" 
              startIcon={<CloseIcon />}
              onClick={() => {
                setSelectedProfessional('all');
                setSelectedPlace('all');
                setSelectedStatus('all');
              }}
              sx={{ minWidth: 140 }}
            >
              Limpar Filtros
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <IconButton onClick={handlePreviousWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          
          <Box display="flex" gap={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={handleToday}>
              Hoje
            </Button>
            <Typography variant="body1" fontWeight="medium">
              {formatMonthYear(currentWeekStart)}
            </Typography>
          </Box>

          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <Box 
            display="grid" 
            gridTemplateColumns={`repeat(${daysToShow}, 1fr)`} 
            gap={1}
          >
            {days.map((day, index) => {
              const { weekday, dayNum } = formatDayHeader(day);
              const dayAppointments = getAppointmentsForDay(day);
              
              return (
                <Box 
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: isToday(day) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: isToday(day) ? 'primary.50' : 'background.paper'
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 1, 
                      textAlign: 'center',
                      bgcolor: isToday(day) ? 'primary.main' : 'grey.100',
                      color: isToday(day) ? 'white' : 'text.primary'
                    }}
                  >
                    <Typography variant="caption" display="block" textTransform="capitalize">
                      {weekday}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {dayNum}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1, minHeight: 200 }}>
                    {dayAppointments.length === 0 ? (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        textAlign="center" 
                        display="block"
                        sx={{ mt: 4 }}
                      >
                        Sem agendamentos
                      </Typography>
                    ) : (
                      dayAppointments.map((apt) => (
                        <AppointmentCard
                          key={apt.id}
                          appointment={apt}
                          onConfirm={handleConfirmClick}
                          onCancel={handleCancelClick}
                          onReschedule={handleRescheduleClick}
                          onMarkAttended={handleMarkAttendedClick}
                        />
                      ))
                    )}
                    
                    {/* Botão para adicionar novo agendamento nesta data */}
                    {!isPastDay(day) && (
                      <Box 
                        sx={{ 
                          mt: 1, 
                          pt: 1, 
                          borderTop: '1px dashed',
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => {
                            setSelectedDate(day);
                            setShowForm(true);
                          }}
                          sx={{ 
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.50'
                            }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Modal auto-suficiente - carrega próprios dados */}
      <AppointmentFormDialog
        open={showForm}
        onClose={handleFormClose}
        onSuccess={handleAppointmentCreated}
        initialData={rescheduleData}
        preselectedDate={selectedDate}
      />

      {/* Dialog de Confirmação */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, appointment: null })}>
        <DialogTitle>Confirmar Agendamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja confirmar o agendamento de <strong>{confirmDialog.appointment?.clientName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, appointment: null })}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmAppointment} variant="contained" color="success">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Marcar como Atendido */}
      <Dialog open={markAttendedDialog.open} onClose={() => setMarkAttendedDialog({ open: false, appointment: null })}>
        <DialogTitle>Marcar como Atendido</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirmar que <strong>{markAttendedDialog.appointment?.clientName}</strong> compareceu ao agendamento?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkAttendedDialog({ open: false, appointment: null })}>
            Cancelar
          </Button>
          <Button onClick={handleMarkAttended} variant="contained" color="info">
            Marcar como Atendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Cancelamento */}
      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, appointment: null })}>
        <DialogTitle>Cancelar Agendamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja cancelar o agendamento de <strong>{cancelDialog.appointment?.clientName}</strong>?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, appointment: null })}>
            Não
          </Button>
          <Button onClick={handleCancelAppointment} variant="contained" color="error">
            Sim, Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de Notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
